import React from "react"
import PropTypes from "prop-types"
import InfoDataPanel from "src/v2/components/InfoDataPanel/info-data-panel"
import { EMPRESAS_V2_PAGE } from "src/utils/routes"
import { cleanDocument } from "src/v2/utils/cnpj-root"

const formatCPFCNPJ = (value) => {
    if (!value) return "-"

    const digits = value.replace(/\D/g, "")

    if (digits.length === 8) {
        return digits.replace(/(\d{2})(\d{3})(\d{3})/, "$1.$2.$3")
    }

    if (digits.length === 11) {
        return digits.replace(
            /(\d{3})(\d{3})(\d{3})(\d{2})/,
            "$1.$2.$3-$4"
        )
    }

    if (digits.length === 14) {
        return digits.replace(
            /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
            "$1.$2.$3/$4-$5"
        )
    }

    return value
}

const formatRaizCNPJ = (value) => {
    if (!value) return "-"

    return `RAIZ CNPJ ${value}`
}

const formatCounterpartyValue = (documentKey) => function counterpartyValueFormatter(value, data) {
    const name = String(value ?? "").trim()
    const document = cleanDocument(data?.[documentKey])

    if (!name || name === "-" || !document) return value ?? "-"

    const searchParams = new URLSearchParams({ cpf_cnpj: document })

    return (
        <a
            href={`${EMPRESAS_V2_PAGE}?${searchParams.toString()}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit" }}
        >
            {value}
        </a>
    )
}

const cnpjOptionalTextProps = {
    optionalTextOpacity: 0.6,
    optionalTextVariant: "captionInfo",
}

const sections = [
    {
        variant: "divided",
        items: [
            {
                key: "Emissor",
                label: "Emissor",
                formatter: formatCounterpartyValue("CPFCNPJ_Emissor"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_Emissor),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Devedor",
                label: "Devedor",
                formatter: formatCounterpartyValue("CPFCNPJ_Devedor"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_Devedor),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "GrupoEconomico",
                label: "Grupo Econômico",
                optionalTextFormatter: (data) =>
                    formatRaizCNPJ(data?.GrupoEconomico_RaizCNPJ),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Setor",
                label: "Setor",
                formatter: (_, data) => data?.Setor ?? "-",
                optionalTextLabel: "Subsetor",
                optionalTextFormatter: (data) => data?.Subsetor ?? "-",
            },
        ],
    },
    {
        items: [
            {
                key: "ResumoDevedor",
                label: "Resumo Devedor",
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
        ],
    },
    {
        items: [
            {
                key: "Coobrigacao",
                label: "Coobrigação",
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
        ],
    },
    {
        variant: "divided",
        items: [
            {
                key: "AgenteFiduciario",
                label: "Agente Fiduciário",
                formatter: formatCounterpartyValue("CPFCNPJ_AgenteFiduciario"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_AgenteFiduciario),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "AgenciaRating",
                label: "Agência de Rating",
                formatter: formatCounterpartyValue("CPFCNPJ_AgenciaRating"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_AgenciaRating),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Auditoria",
                label: "Auditoria",
                formatter: formatCounterpartyValue("CPFCNPJ_Auditoria"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_Auditoria),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "ContaRecebedora",
                label: "Conta Recebedora",
                formatter: formatCounterpartyValue("CPFCNPJ_IFContaRecebedora"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_IFContaRecebedora),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
        ],
    },
    {
        variant: "divided",
        items: [
            {
                key: "Custodiante",
                label: "Custodiante",
                formatter: formatCounterpartyValue("CPFCNPJ_Custodiante"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_Custodiante),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Cedente",
                label: "Cedente",
                formatter: formatCounterpartyValue("CPFCNPJ_Cedente"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_Cedente),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Depositario",
                label: "Depositário",
                formatter: formatCounterpartyValue("CPFCNPJ_Depositario"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_Depositario),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
            {
                key: "Escriturador",
                label: "Escriturador",
                formatter: formatCounterpartyValue("CPFCNPJ_Escriturador"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_Escriturador),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
        ],
    },
    {
        variant: "divided",
        sx: {
            width: { xxxs: "100%", md: "25%" },
        },
        items: [
            {
                key: "Registrador",
                label: "Registrador",
                formatter: formatCounterpartyValue("CPFCNPJ_Registrador"),
                optionalTextFormatter: (data) =>
                    formatCPFCNPJ(data?.CPFCNPJ_Registrador),
                ...cnpjOptionalTextProps,
                gridProps: { xxxs: 2, sm: 2, md: true },
                sx: { pr: 2 },
            },
        ],
    },
]

export const ContrapartesPanel = ({ contraparteData }) => {
    return (
        <InfoDataPanel
            title="Contrapartes: CPF, CNPJ"
            titleSx={{
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontWeight: 700,
                fontSize: "1rem",
            }}
            data={contraparteData}
            sections={sections}
        />
    )
}

ContrapartesPanel.propTypes = {
    contraparteData: PropTypes.shape({
        Emissor: PropTypes.string,
        Devedor: PropTypes.string,
        GrupoEconomico: PropTypes.string,
        Setor: PropTypes.string,
        SubSetor: PropTypes.string,
        ResumoDevedor: PropTypes.string,
        Coobrigacao: PropTypes.string,
        AgenteFiduciario: PropTypes.string,
        AgenciaRating: PropTypes.string,
        Auditoria: PropTypes.string,
        ContaRecebedora: PropTypes.string,
        Custodiante: PropTypes.string,
        Cedente: PropTypes.string,
        Depositario: PropTypes.string,
        Escriturador: PropTypes.string,
        Registrador: PropTypes.string,

        GrupoEconomico_RaizCNPJ: PropTypes.string,
        CPFCNPJ_Emissor: PropTypes.string,
        CPFCNPJ_Devedor: PropTypes.string,
        CPFCNPJ_AgenteFiduciario: PropTypes.string,
        CPFCNPJ_AgenciaRating: PropTypes.string,
        CPFCNPJ_Auditoria: PropTypes.string,
        CPFCNPJ_IFContaRecebedora: PropTypes.string,
        CPFCNPJ_Custodiante: PropTypes.string,
        CPFCNPJ_Cedente: PropTypes.string,
        CPFCNPJ_Depositario: PropTypes.string,
        CPFCNPJ_Escriturador: PropTypes.string,
        CPFCNPJ_Registrador: PropTypes.string,
    }),
}
